import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter, useRouteError, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { TitleProvider, CartProvider, WebdataProvider, UseProvider } from './HooksPublic'
import { Header, Icon } from 'react-frontier'
import { Navbar, Footer } from './components';
import { SPECIAL_CATEGORIES } from './UtilPublic';
import { TrackerProvider } from '@cocoan/components/TrackerHook';
import { GOOGLE_TAG, META_PIXEL } from './ConfigPublic';
import { PayfrontProvider } from 'react-payfront';
import classNames from 'classnames';

import {
	Checkout,
	Landing,
	PaymentInstructions,
	ProductView,
	Shop,
	InvoiceCreate,
	VisitUs,
	HelpView,
	TermsView,
	SearchOrders,
	CheckoutLink,
	CheckoutResponse,
	ReceiptView,
	Page,
	OrderView,
	PublicOrder,
	WorkWithUs,
	CareerView,
	LoginResponse,
	CustomizedBusiness,
	CustomizedSocial,
	Rewards,
	AboutUs,
} from './screens';

import 'fomantic-ui-css/semantic.css';
import 'frontier-css/css/frontier.css';
import './style/site.scss'
import UserAccount from './screens/UserAccount';


var ErrorElement = ()=>{
	const error = (useRouteError() as any);
	return <Header
		text={error.status}
		subtext={error.status===404 ? 'Página no encontrada' : `Hubo un error inesperado. (PBL-LCL-${error.status})`}
		style={{ fontSize: 100 }}
		subheaderStyle={{ fontSize: 20 }}
		containerStyle={{ marginTop: 15 }}
	/>
}

var SiteContainer = (props: { outlet: JSX.Element })=>{
	var location = useLocation();
	var fullscreen = ['/checkout'];
	var no_navbar = ['/r/'];
	var IS_FULLSCREEN = fullscreen.findIndex(a=>location.pathname.startsWith(a))!==-1;
	var HAS_NAVBAR = no_navbar.findIndex(a=>location.pathname.startsWith(a))===-1;
	
	return <TitleProvider suffix='COCOAN'>
		<PayfrontProvider apiKey={null} applePay>
			<TrackerProvider pixelId={META_PIXEL} gtag={GOOGLE_TAG}>
				<WebdataProvider>
					<CartProvider>
						{HAS_NAVBAR && <Navbar />}
						<div className={classNames('co body-container', {
							fullscreen: IS_FULLSCREEN,
						})}>
							{props.outlet}
						</div>
						{!IS_FULLSCREEN && HAS_NAVBAR && <Footer />}
						<a
							className='co wpp-float'
							href={`https://wa.me/528129448353?text=%C2%A1Hola!%20Vengo%20de%20la%20p%C3%A1gina%2C%20me%20gustar%C3%ADa%20tener%20m%C3%A1s%20informaci%C3%B3n.`}
							target='_blank'
							rel='noopener noreferrer'
							aria-label='Contáctanos por WhatsApp'
						>
							<Icon name='whatsapp' size={30} color='white' />
							<span className='wpp-tooltip'>¿Necesitas ayuda?</span>
						</a>
					</CartProvider>
				</WebdataProvider>
			</TrackerProvider>
		</PayfrontProvider>
	</TitleProvider>
}

const Router = createBrowserRouter([{
	path: '/',
	errorElement: <SiteContainer outlet={<ErrorElement />} />,
	element: <SiteContainer outlet={<Outlet />} />,
	children: [
		{ path: '/', element: <Landing /> },
		{ path: '/auth', element: <LoginResponse /> },
		{ path: '/account', element: <UserAccount /> },
		{ path: '/shop', element: <Shop /> },
		{ path: '/shop/:category', element: <Shop /> },
		{ path: '/facturar', element: <InvoiceCreate /> },
		{ path: '/search', element: <Shop search /> },
		{ path: '/product/:id', element: <ProductView /> },
		{ path: '/checkout/link/:token', element: <CheckoutLink /> },
		{ path: '/checkout/:token/openpay', element: <CheckoutResponse /> },
		{ path: '/order/:hash/finish', element: <PublicOrder /> },
		{ path: '/checkout*', element: <Checkout /> },
		{ path: '/order/:order_hash', element: <OrderView /> },
		{ path: '/orders/:order_hash', element: <OrderView /> },
		{ path: '/quotation/:order_hash', element: <OrderView /> },
		{ path: '/order/:hash/payment', element: <PaymentInstructions /> },
		{ path: '/ayuda', element: <HelpView /> },
		{ path: '/terminos', element: <TermsView /> },
		{ path: '/visitanos', element: <VisitUs /> },
		{ path: '/vacantes', element: <WorkWithUs /> },
		{ path: '/vacantes/:id', element: <CareerView /> },		
		{ path: '/ordenes', element: <SearchOrders /> },
		{ path: '/r/:hash', element: <ReceiptView /> },
		{ path: '/personalizados-empresariales', element: <CustomizedBusiness /> },
		{ path: '/personalizados-sociales', element: <CustomizedSocial /> },
		{ path: '/rewards', element: <Rewards /> },
		{ path: '/nosotros', element: <AboutUs /> },

		// === SPECIAL CATEGORIES ===
		...SPECIAL_CATEGORIES.map(a=>(
			{ path: a.route, element: (
				<Shop id={a.category} />
			)}
		)),

		// === PDFS ===
		{ path: '/patrones', element: <Shop /> },
		{ path: '/*', element: <Page /> },
	],
}]);

var Root = ()=>{
	return (
		<UseProvider>
			<Toaster position="bottom-right" toastOptions={{
				duration: 5000,
				error: {
					iconTheme: {
						primary: 'white',
						secondary: '#ff4b4b'
					},
					style: {
						color: 'white',
						background: '#ff4b4b',
					}
				},
				success: {
					iconTheme: {
						primary: 'white',
						secondary: 'rgb(89, 179, 67)'
					},
					style: {
						color: 'white',
						background: 'rgb(89, 179, 67)'
					}
				}
			}} />
			<RouterProvider router={Router}/>
		</UseProvider>
	)
}

if(process.env.NODE_ENV==='production'){
	var checkBuild = ()=>fetch('/asset-manifest.json', { cache: 'no-store' }).then(r=>r.text()).then(manifest=>{
		var script = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]');
		if(!script) return;
		if(manifest.includes(new URL(script.src).pathname)){
			return sessionStorage.removeItem('build_reload');
		}
		if(sessionStorage.getItem('build_reload')) return;
		sessionStorage.setItem('build_reload', '1');
		window.location.reload();
	}).catch(()=>null);

	checkBuild();
	document.addEventListener('visibilitychange', ()=>{
		if(!document.hidden && !window.location.pathname.startsWith('/checkout')) checkBuild();
	});
}

const root = ReactDOM.createRoot(
	document.getElementById('root') as HTMLElement
);

root.render(<Root />);